import React, { ReactElement, useEffect } from 'react';
import {
  Table,
  TableCell,
  TableHead,
  TableBody,
  TableRow,
  makeStyles,
  createStyles,
  CircularProgress,
  CardHeader,
  Typography,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { UseRestApiResult } from 'hooks/useRestApi.types';
import { DdaIpsData } from 'App.types';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { useTenantContext } from 'contexts/TenantContext';

interface DdaIpsResp extends Omit<UseRestApiResult, 'data'> {
  data: {
    items: DdaIpsData[];
  };
}

export const useStyles = makeStyles(() =>
  createStyles({
    heading: {
      marginBottom: '14px',
    },
    section: {
      marginTop: '24px',
    },
    table: {
      width: '50%',
    },
  }),
);

export const DdaDefinitionsIpLists: React.FC = (props) => {
  const { t } = useTranslation();
  const classes = useStyles(props);
  const { tenant } = useTenantContext();
  const { learnInstanceId } = useLearnInstance();
  const tenantId = tenant?.tenantType === 'Learn' ? learnInstanceId : tenant?.id;
  const {
    data: ddaIps,
    error,
    loading,
    fetch,
  } = useRestApi(apiUrl('lct', `dda/tenants/${tenantId}/ips`), {
    manual: true,
  }) as DdaIpsResp;
  let content: ReactElement;

  useEffect(() => {
    if (tenantId) {
      fetch();
    }
  }, [fetch, tenantId]);

  const loader = () => (
    <CircularProgress
      ariaLabel={t('global.loadingIndicator.loading').toString()}
      data-testid="loading-dda-ips"
    />
  );

  const erroMessage = () => (
    <Typography data-testid="dda-ip-info-error">{t('global.fetchError')}</Typography>
  );

  const noDdaIpMessage = () =>
    (content = <Typography data-testid="dda-ip-list-no-data">{t('ddaIpList.noData')}</Typography>);

  const ipTable = (ddaIps: DdaIpsData[]) => (
    <Table className={classes.table} data-testid="dda-ips-datatable">
      <TableHead>
        <TableRow>
          <TableCell>{t('ddaIpList.id')}</TableCell>
          <TableCell align="justify">{t('ddaIpList.ipNumber')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {ddaIps.map((ip: DdaIpsData) => (
          <TableRow key={ip.id}>
            <TableCell>{ip.id}</TableCell>
            <TableCell align="justify">{ip.ipAddress}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (!tenantId) {
    content = loader();
  } else if (error) {
    content = erroMessage();
  } else if (loading) {
    content = loader();
  } else if (!ddaIps?.items.length) {
    content = noDdaIpMessage();
  } else {
    content = ipTable(ddaIps.items);
  }

  return (
    <div data-testid="dda-ip-list-section" className={classes.section}>
      <CardHeader
        title={t('ddaIpList.pageTitle')}
        titleTypographyProps={{
          color: 'textSecondary',
          component: 'h3' as any,
          variant: 'h4',
        }}
        className={classes.heading}
      />
      {content}
    </div>
  );
};
